import React from "react";
import { Box, Grid, Typography, Container, Button, useMediaQuery } from "@mui/material";
import { appTheme } from "shared/theme";
import { useNavigate } from "react-router-dom";
import { scroller } from "react-scroll";
import { useTheme } from "@mui/material/styles";

export const CareersContent = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const isIpadLandscape = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const handleContact = () => {
    navigate("/");
    setTimeout(() => {
      scroller.scrollTo("contact", {
        smooth: true,
        offset: 100,
        duration: 2000,
        delay: 0,
      });
    }, 0);
  };

  const viewTeam = () => {
    navigate("/team");
  };

  const ValuePill = ({ title, body }) => (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid rgba(20,55,74,0.10)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.72) 100%)",
        backdropFilter: "blur(10px)",
        px: 2.2,
        py: 1.8,
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontFamily:
            appTheme.typography?.h1?.fontFamily || appTheme.typography?.body1?.fontFamily,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: appTheme.palette.blue.main,
          fontSize: 12,
          mb: 0.9,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: appTheme.typography?.body1?.fontFamily,
          color: appTheme.palette.gray.main,
          lineHeight: 1.7,
          fontSize: 14,
        }}
      >
        {body}
      </Typography>
    </Box>
  );

  return (
    <Box
      id="careers"
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        ml: "-50vw",
        mr: "-50vw",
        overflow: "hidden",
        backgroundColor: appTheme.palette.white.main,
        minHeight: "100svh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 10, sm: 12, md: 12 },
          pb: { xs: 8, sm: 9, md: isIpadLandscape ? 7 : 9 },
          background: {
            xs: appTheme.palette.blue.main,
            md: "radial-gradient(900px 520px at 10% 0%, rgba(92,181,179,0.18) 0%, rgba(255,255,255,0) 60%), radial-gradient(900px 520px at 100% 10%, rgba(0,0,0,0.04) 0%, rgba(255,255,255,0) 55%)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "#F9F4F3",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: `linear-gradient(180deg, ${appTheme.palette.yellow.main} 0%, ${appTheme.palette.yellow.main} 62%, rgba(249,244,243,1) 62%, rgba(249,244,243,1) 100%)`,
              md: `linear-gradient(90deg, ${appTheme.palette.yellow.main} 0%, ${appTheme.palette.yellow.main} 58%, rgba(249,244,243,1) 58%, rgba(249,244,243,1) 100%)`,
            },
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: `radial-gradient(circle at 20% 20%, rgba(20,55,74,0.12) 0%, rgba(20,55,74,0) 55%)`,
              md: `radial-gradient(circle at 18% 50%, rgba(20,55,74,0.12) 0%, rgba(20,55,74,0) 55%)`,
            },
            zIndex: 2,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 3 }}>
          <Grid
            container
            spacing={{ xs: 3, md: 5 }}
            alignItems="center"
            sx={{ textAlign: { xs: "center", md: "left" } }}
          >
            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { xs: 0, md: 2 } }}>
                <Typography
                  sx={{
                    fontFamily:
                      appTheme.typography?.h1?.fontFamily || appTheme.typography?.body1?.fontFamily,
                    textTransform: "uppercase",
                    fontWeight: 900,
                    letterSpacing: "0.05em",
                    color: appTheme.palette.blue.main,
                    lineHeight: 1.05,
                    fontSize: { xs: 30, sm: 40, md: 42, lg: 58 },
                    mb: 2,
                  }}
                >
                  Where work meets adventure.
                </Typography>

                <Typography
                  sx={{
                    mt: 1.6,
                    fontSize: { xs: 14, sm: 16, md: 16 },
                    color: "rgba(20,55,74,0.88)",
                    lineHeight: 1.8,
                    maxWidth: 520,
                    fontFamily: appTheme.typography?.body1?.fontFamily,
                    mx: { xs: "auto", md: 0 },
                  }}
                >
                  Corporate culture matters. Smart creatives care about the place they work — and the
                  experiences they share.
                </Typography>

                <Box
                  sx={{
                    mt: 2.6,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.4,
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "center", md: "flex-start" },
                    maxWidth: { xs: "100%", sm: 520 },
                    mx: { xs: "auto", md: 0 },
                  }}
                >
                  <Button
                    onClick={handleContact}
                    sx={{
                      backgroundColor: appTheme.palette.blue.main,
                      color: appTheme.palette.white.main,
                      px: 2.2,
                      py: 1.15,
                      borderRadius: 3,
                      fontWeight: 900,
                      letterSpacing: ".08rem",
                      textTransform: "uppercase",
                      "&:hover": {
                        color: appTheme.palette.yellow.main,
                        backgroundColor: appTheme.palette.blue.main,
                        opacity: 0.92,
                      },
                    }}
                  >
                    Get in touch
                  </Button>

                  <Button
                    onClick={viewTeam}
                    variant="outlined"
                    sx={{
                      px: 2.2,
                      py: 1.15,
                      borderRadius: 3,
                      borderColor: "rgba(20,55,74,0.18)",
                      color: appTheme.palette.blue.main,
                      backgroundColor: "rgba(255,255,255,0.55)",
                      fontWeight: 900,
                      letterSpacing: ".08rem",
                      textTransform: "uppercase",
                      "&:hover": {
                        borderColor: appTheme.palette.blue.main,
                        backgroundColor: appTheme.palette.blue.main,
                        color: appTheme.palette.white.main,
                      },
                    }}
                  >
                    Meet the team
                  </Button>

                  <Box
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      alignItems: "center",
                      gap: 1,
                      color: "rgba(20,55,74,0.78)",
                      fontSize: { xs: 12, sm: 13 },
                      fontFamily: appTheme.typography?.body1?.fontFamily,
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  alignItems: "center",
                  pt: { xs: 1, md: 0 },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: -10, md: "12%" },
                    right: { xs: "auto", md: "10%" },
                    width: { xs: 260, sm: 320, md: isIpadLandscape ? 320 : 360 },
                    height: { xs: 260, sm: 320, md: isIpadLandscape ? 320 : 360 },
                    borderRadius: "50%",
                    background: "rgba(20,55,74,0.10)",
                    filter: "blur(2px)",
                  }}
                />

                <Box
                  sx={{
                    width: { xs: "92%", sm: "78%", md: "100%" },
                    maxWidth: { md: isIpadLandscape ? 520 : 560, lg: 620, xl: 720 },
                    borderRadius: { xs: 5, md: 6 },
                    overflow: "hidden",
                    boxShadow: "0 20px 60px rgba(20,55,74,0.18)",
                    backgroundColor: "rgba(255,255,255,0.35)",
                    border: "1px solid rgba(255,255,255,0.45)",
                    backdropFilter: "blur(10px)",
                    px: { xs: 2, sm: 3, md: 3.5 },
                    py: { xs: 2, sm: 3, md: 3.5 },
                  }}
                >
                  <Box
                    component="img"
                    src="/imgs/sitegraphics/peopleChatting.png"
                    alt="people chatting"
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      objectFit: "contain",
                      borderRadius: { xs: 4, md: 4 },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          py: { xs: 7, md: isIpadLandscape ? 6 : 9 },
          background:
            "radial-gradient(900px 520px at 50% 0%, rgba(92,181,179,0.10) 0%, rgba(255,255,255,0) 55%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                <Typography
                  sx={{
                    fontSize: { xs: 26, sm: 32, md: 38 },
                    fontWeight: 900,
                    fontFamily:
                      appTheme.typography?.h1?.fontFamily || appTheme.typography?.body1?.fontFamily,
                    color: appTheme.palette.blue.main,
                    mb: 1.6,
                    lineHeight: 1.1,
                  }}
                >
                  Awaken your adventurous side.
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: 15, md: 16.5 },
                    color: "rgba(20,55,74,0.85)",
                    lineHeight: 1.9,
                    mb: 2.2,
                    fontFamily: appTheme.typography?.body1?.fontFamily,
                    maxWidth: 760,
                    mx: { xs: "auto", md: 0 },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  7DAY is reimagining how companies reward and recharge their teams. We connect
                  businesses with unique spaces and experiences — making it easy to plan everything
                  from offsites to personal getaways.
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: 15, md: 16.5 },
                    color: "rgba(20,55,74,0.85)",
                    lineHeight: 1.9,
                    fontFamily: appTheme.typography?.body1?.fontFamily,
                    maxWidth: 760,
                    mx: { xs: "auto", md: 0 },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Based in Australia, we value high performance and show appreciation for hard work
                  — with the flexibility to work anywhere, anytime, or from our office.
                </Typography>

        

                <Box sx={{ mt: 3.2, display: { xs: "flex", md: "none" }, justifyContent: "center" }}>
                  <Button
                    onClick={handleContact}
                    sx={{
                      px: 2.8,
                      py: 1.25,
                      borderRadius: 3,
                      backgroundColor: appTheme.palette.blue?.main,
                      color: appTheme.palette.white?.main,
                      fontWeight: 900,
                      letterSpacing: ".10rem",
                      textTransform: "uppercase",
                      "&:hover": {
                        opacity: 0.9,
                        backgroundColor: appTheme.palette.yellow?.main,
                        color: appTheme.palette.blue?.main,
                      },
                    }}
                  >
                    Contact us
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <ValuePill
                  title="Culture first"
                  body="We build for teams who care about the way work feels — not just where it happens."
                />
                <ValuePill
                  title="Flexible by default"
                  body="Work from anywhere, anytime, or from our office — outcomes over clock-watching."
                />
                <ValuePill
                  title="Opportunities"
                  body="When roles open up, you’ll find them here — and we’ll keep it simple and human."
                />
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: { xs: 5, md: isIpadLandscape ? 4 : 6 },
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 4,
              border: "1px solid rgba(20,55,74,0.10)",
              backgroundColor: "#F7FAFB",
              boxShadow: "0 14px 40px rgba(20,55,74,0.06)",
              // maxWidth: 980,
              mx: "auto",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  sx={{
                    color: appTheme.palette.yellow.main,
                    fontFamily: appTheme.typography?.body1?.fontFamily,
                    letterSpacing: ".14rem",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    fontSize: 11.5,
                  }}
                >
                  Available positions
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    color: appTheme.palette.blue.main,
                    fontFamily: appTheme.typography?.body1?.fontFamily,
                    fontSize: { xs: 14, md: 16 },
                  }}
                >
                  We are currently not hiring.
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 1.2,
                  flexWrap: "wrap",
                }}
              >
                

                <Button
                  onClick={handleContact}
                  sx={{
                    px: 2.8,
                    py: 1.2,
                    borderRadius: 3,
                    backgroundColor: appTheme.palette.blue?.main,
                    color: appTheme.palette.white?.main,
                    fontWeight: 900,
                    letterSpacing: ".10rem",
                    textTransform: "uppercase",
                    "&:hover": {
                      opacity: 0.9,
                      backgroundColor: appTheme.palette.yellow?.main,
                      color: appTheme.palette.blue?.main,
                    },
                  }}
                >
                  Contact us
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};